import { motion } from "framer-motion";
import { ArrowDown, Star } from "@phosphor-icons/react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1762291629616-3e2c044c79a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwbHV4dXJpb3VzJTIwaGFpciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzQ0NzAwOXww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* background image */}
      <motion.div
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMG}
          alt="Luxurious hair portrait"
          className="w-full h-full object-cover object-[70%_center]"
        />
      </motion.div>

      {/* gradient overlay */}
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 grain pointer-events-none" />

      {/* content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 flex flex-col justify-end min-h-[100svh] pb-20 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#E6CC98]" />
          <span
            data-testid="hero-overline"
            className="text-xs sm:text-sm uppercase tracking-[0.3em] gold-text font-body"
          >
            Made in America · Since 2023
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="font-heading font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-[#FDFBF7] max-w-4xl"
        >
          Hair, hand-crafted
          <br />
          <span className="italic font-normal gold-gradient-text">
            on American soil.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#A39791] font-body"
        >
          Kim Monroe LLC curates premium retail wigs and bundles — meticulously
          made in the United States. Every strand a quiet rebellion against the
          ordinary.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <a
            href="#collection"
            data-testid="hero-cta-collection"
            className="btn-gold px-10 py-4 text-xs uppercase tracking-[0.3em]"
          >
            Explore the Collection
          </a>
          <a
            href="#waitlist"
            data-testid="hero-cta-waitlist"
            className="btn-outline-gold px-10 py-4 text-xs uppercase tracking-[0.3em]"
          >
            Join the Waitlist
          </a>
        </motion.div>

        {/* scroll cue + reviews badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 flex items-center justify-between flex-wrap gap-6"
        >
          <div className="flex items-center gap-3 text-[#A39791] text-xs font-body tracking-wide">
            <ArrowDown size={16} weight="thin" className="animate-bounce" />
            <span className="uppercase tracking-[0.25em]">Scroll</span>
          </div>
          <div className="flex items-center gap-3 border border-white/10 bg-white/[0.03] backdrop-blur-md px-5 py-3">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  weight="fill"
                  className="text-[#E6CC98]"
                />
              ))}
            </div>
            <span className="text-xs text-[#FDFBF7] font-body">
              <span className="font-medium">4.96</span>
              <span className="text-[#A39791]"> · 2,400+ clients styled</span>
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
